import {
  ACTIVE_ABILITY_DEFINITIONS,
  BALL_FUSION_DEFINITIONS,
  BOSS_DEFINITION,
  BRICK_DEFINITIONS,
  BUILD_DEFINITIONS,
  ELITE_WAVE_DEFINITIONS,
  GAMEPLAY_CONFIG,
  RELIC_DEFINITIONS,
  RISK_CONTRACT_DEFINITIONS,
  RUN_CLEANSE_DEFINITION,
  RUN_CURSE_DEFINITIONS,
  RUN_EVENT_DEFINITIONS,
  UPGRADE_DEFINITIONS,
} from '@brick-rogue/game-content';
import { CYBERPUNK_VISUAL_ASSETS } from '@brick-rogue/game-content/visual-assets';
import { Link } from 'react-router';

import { AppHeader } from './AppHeader';
import { useReducedMotionPreference } from '../features/settings/uiPreferencesStore';

interface HomeItem {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly imageUrl: string;
  readonly secondaryImageUrl?: string;
  readonly label: string;
}

const UPGRADE_ITEMS: readonly HomeItem[] = Object.values(UPGRADE_DEFINITIONS).map((upgrade) => ({
  id: upgrade.id,
  name: upgrade.name,
  description: upgrade.description,
  imageUrl: CYBERPUNK_VISUAL_ASSETS.upgrades[upgrade.id],
  label: upgrade.category === 'foundation' ? '基础强化' : '球芯模块',
}));

const HOME_ITEM_GROUPS: readonly {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly items: readonly HomeItem[];
}[] = [
  {
    id: 'bricks',
    title: '战场砖块',
    summary: '轮廓代表耐久和抗性；带道具的特殊砖块会在击碎后立即触发效果。',
    items: Object.values(BRICK_DEFINITIONS).map((brick) => ({
      id: brick.id,
      name: brick.name,
      description: brick.description,
      imageUrl: CYBERPUNK_VISUAL_ASSETS.gameplay.bricks[brick.id],
      label: brick.rewardEffectId ? '击碎触发道具' : `BRICK · HP ${brick.hitPoints}`,
    })),
  },
  {
    id: 'foundation',
    title: '基础强化',
    summary: '直接提升弹丸数量、伤害、速度、挡板或生命。',
    items: UPGRADE_ITEMS.filter((item) => item.label === '基础强化'),
  },
  {
    id: 'cores',
    title: '球芯与增幅',
    summary: '改变弹丸命中方式；先获得球芯，随后可选择对应增幅。',
    items: UPGRADE_ITEMS.filter((item) => item.label === '球芯模块'),
  },
  {
    id: 'fusions',
    title: '球芯融合',
    summary: '同时持有两种匹配球芯后自动激活；双图直接显示融合配方。',
    items: Object.values(BALL_FUSION_DEFINITIONS).map((fusion) => ({
      id: fusion.id,
      name: fusion.name,
      description: fusion.description,
      imageUrl: CYBERPUNK_VISUAL_ASSETS.ballCores[fusion.requiredCoreIds[0]],
      secondaryImageUrl: CYBERPUNK_VISUAL_ASSETS.ballCores[fusion.requiredCoreIds[1]],
      label: '双球芯融合',
    })),
  },
  {
    id: 'relics',
    title: 'Boss 遗物',
    summary: 'Boss 奖励中的本局规则级改造；附咒遗物会同时绑定限时风险。',
    items: Object.values(RELIC_DEFINITIONS).map((relic) => {
      const isCursedRelic = Boolean(relic.curseId);
      return {
        id: relic.id,
        name: relic.name,
        description: relic.description,
        imageUrl: CYBERPUNK_VISUAL_ASSETS.relics[relic.id],
        label: isCursedRelic ? '附咒遗物 · 3 关风险' : 'Boss 遗物',
      };
    }),
  },
  {
    id: 'abilities',
    title: '主动技能',
    summary: '战斗中主动释放，分别负责分裂、防守和全场破坏。',
    items: Object.values(ACTIVE_ABILITY_DEFINITIONS).map((ability) => ({
      id: ability.id,
      name: ability.name,
      description: ability.description,
      imageUrl: CYBERPUNK_VISUAL_ASSETS.abilities[ability.id],
      label: `${ability.shortcut} · ${ability.cooldownMilliseconds / 1000}s`,
    })),
  },
  {
    id: 'events',
    title: '种子事件',
    summary: '固定关卡出现的可复现交易；可以接受，也可以不付代价安全离开。',
    items: Object.values(RUN_EVENT_DEFINITIONS).map((event) => ({
      id: event.id,
      name: event.name,
      description: `${event.description} ${event.costLabel}，${event.rewardLabel}。`,
      imageUrl: CYBERPUNK_VISUAL_ASSETS.events[event.id],
      label: '局间事件',
    })),
  },
  {
    id: 'curses',
    title: '限时诅咒',
    summary: '事件或附咒遗物带来的限时规则，风险和收益会一起持续。',
    items: Object.values(RUN_CURSE_DEFINITIONS).map((curse) => ({
      id: `curse-${curse.id}`,
      name: curse.name,
      description: curse.description,
      imageUrl:
        curse.id === 'reactor-instability'
          ? CYBERPUNK_VISUAL_ASSETS.relics['cursed-reactor']
          : CYBERPUNK_VISUAL_ASSETS.events['glass-overclock'],
      label: curse.source === 'relic' ? '遗物诅咒' : '事件诅咒',
    })),
  },
  {
    id: 'cleanse',
    title: '稳定净化',
    summary: '净化会移除限时诅咒；附咒遗物也会随绑定诅咒一并被销毁。',
    items: [
      {
        id: RUN_CLEANSE_DEFINITION.id,
        name: RUN_CLEANSE_DEFINITION.name,
        description: RUN_CLEANSE_DEFINITION.description,
        imageUrl: CYBERPUNK_VISUAL_ASSETS.cleanse[RUN_CLEANSE_DEFINITION.id],
        label: '净化节点',
      },
    ],
  },
  {
    id: 'elite-protocols',
    title: '精英协议',
    summary: '第 5、15、25 关的战斗修正，承担额外压力换取更丰厚奖励。',
    items: Object.values(ELITE_WAVE_DEFINITIONS).map((elite) => ({
      id: elite.id,
      name: elite.name,
      description: `${elite.description} ${elite.riskLabel}；${elite.rewardLabel}。`,
      imageUrl:
        elite.id === 'fortress-matrix'
          ? CYBERPUNK_VISUAL_ASSETS.gameplay.bricks.reinforced
          : elite.id === 'overclock-storm'
            ? CYBERPUNK_VISUAL_ASSETS.upgrades['overclock-drive']
            : CYBERPUNK_VISUAL_ASSETS.ballCores.phase,
      label: '精英关规则',
    })),
  },
  {
    id: 'risk-contracts',
    title: '风险契约',
    summary: 'Boss 后可选择的整幕规则，拒绝不会失去已有奖励。',
    items: Object.values(RISK_CONTRACT_DEFINITIONS).map((contract) => ({
      id: contract.id,
      name: contract.name,
      description: `${contract.description} ${contract.riskLabel}；${contract.rewardLabel}。`,
      imageUrl:
        contract.id === 'siege-bounty'
          ? CYBERPUNK_VISUAL_ASSETS.gameplay.bricks.armored
          : contract.id === 'redline-license'
            ? CYBERPUNK_VISUAL_ASSETS.upgrades['overclock-drive']
            : CYBERPUNK_VISUAL_ASSETS.ballCores.gravity,
      label: '整幕风险规则',
    })),
  },
];

export function HomePage() {
  const reducedMotion = useReducedMotionPreference();

  return (
    <div className="portal-shell" data-motion={reducedMotion ? 'reduced' : 'full'}>
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <div className="portal-ambient portal-ambient--one" />
      <div className="portal-ambient portal-ambient--two" />
      <AppHeader />

      <main id="main-content" className="home-main">
        <section className="home-hero" aria-labelledby="home-title">
          <div className="home-hero__copy">
            <p className="section-kicker">M12.2 · OFFLINE SEASON ROTATION</p>
            <h1 id="home-title">
              一次发射，
              <span>无限构筑。</span>
            </h1>
            <p className="home-hero__lead">
              在三十关动态战场中控制反弹、复制与裂变。完成每周轮换协议、战术任务与成就，点亮可持久化的档案徽章；所有奖励保持横向展示，不提高下一局战力。
            </p>
            <div className="home-actions">
              <Link className="portal-button portal-button--primary" to="/levels">
                选择关卡 <span aria-hidden="true">→</span>
              </Link>
              <Link className="portal-button" to="/codex">
                查看战术图鉴
              </Link>
              <Link className="portal-button" to="/daily">
                今日挑战
              </Link>
              <Link className="portal-button" to="/objectives">
                任务与成就
              </Link>
              <Link className="portal-button" to="/seasons">
                赛季轮换
              </Link>
            </div>
            <dl className="home-stats" aria-label="游戏内容概览">
              <div>
                <dt>{GAMEPLAY_CONFIG.run.maxWaves}</dt>
                <dd>战役关卡</dd>
              </div>
              <div>
                <dt>{Object.keys(BUILD_DEFINITIONS).length}</dt>
                <dd>初始构筑</dd>
              </div>
              <div>
                <dt>24×</dt>
                <dd>瞬时裂变</dd>
              </div>
            </dl>
          </div>

          <div className="home-hero__visual" aria-label="奇点守卫作战预览">
            <div className="orbit orbit--outer" />
            <div className="orbit orbit--inner" />
            <div className="boss-core">
              <img
                src={CYBERPUNK_VISUAL_ASSETS.gameplay.bosses[30]}
                alt={`${BOSS_DEFINITION.name} Boss`}
              />
              <div>
                <span>LEVEL 30</span>
                <strong>{BOSS_DEFINITION.name}</strong>
                <small>BOSS CHECKPOINT</small>
              </div>
            </div>
            <div className="hero-projectile hero-projectile--one" />
            <div className="hero-projectile hero-projectile--two" />
            <div className="hero-projectile hero-projectile--three" />
          </div>
        </section>

        <section className="home-builds" aria-labelledby="builds-title">
          <div className="section-heading">
            <div>
              <p className="section-kicker">CHOOSE YOUR DOCTRINE</p>
              <h2 id="builds-title">三种流派，同一条生存准则</h2>
            </div>
            <p>每个构筑从两项核心强化起步，在波间选择中持续演化。</p>
          </div>
          <div className="home-build-grid">
            {Object.values(BUILD_DEFINITIONS).map((build, index) => (
              <article key={build.id} className="home-build-card" data-build={build.id}>
                <span>0{index + 1}</span>
                <img
                  src={CYBERPUNK_VISUAL_ASSETS.builds[build.id]}
                  alt=""
                  aria-hidden="true"
                  width={64}
                  height={64}
                  loading="lazy"
                />
                <h3>{build.name}</h3>
                <p>{build.description}</p>
                <small>{build.upgradeIds.length} 项起始强化</small>
              </article>
            ))}
          </div>
        </section>

        <section className="home-items" aria-labelledby="items-title">
          <div className="section-heading">
            <div>
              <p className="section-kicker">ITEM SYSTEM</p>
              <h2 id="items-title">道具与战斗模块</h2>
            </div>
            <p>图片、名称和实际效果来自同一份内容定义，游戏新增道具时可在这里同步扩展。</p>
          </div>

          {HOME_ITEM_GROUPS.map((group) => (
            <section
              key={group.id}
              className="home-item-group"
              aria-labelledby={`item-${group.id}`}
            >
              <header>
                <h3 id={`item-${group.id}`}>{group.title}</h3>
                <p>{group.summary}</p>
                <small>{group.items.length} ITEMS</small>
              </header>
              <div className="home-item-grid">
                {group.items.map((item) => (
                  <article key={item.id} className="home-item-card" data-item-id={item.id}>
                    {item.secondaryImageUrl ? (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'grid',
                          width: '4.4rem',
                          height: '4.4rem',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          alignItems: 'center',
                          gap: '0.12rem',
                        }}
                      >
                        <img
                          src={item.imageUrl}
                          alt=""
                          width={34}
                          height={68}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                        <img
                          src={item.secondaryImageUrl}
                          alt=""
                          width={34}
                          height={68}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </span>
                    ) : (
                      <img src={item.imageUrl} alt="" aria-hidden="true" loading="lazy" />
                    )}
                    <div>
                      <small>{item.label}</small>
                      <h4>{item.name}</h4>
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </section>
      </main>

      <footer className="site-footer">
        <span>ROGUE BRICK BREAKER · WEB COMBAT SYSTEM</span>
        <span>鼠标 / 触控 / 键盘</span>
      </footer>
    </div>
  );
}
